import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEmployee } from '../../services/api/employeeServices';
import { getProjectById } from '../../services/api/projectService';
import { getTowerById } from '../../services/api/towerService';
import { FiltersFormData, MonthSelect } from '../../models/reports';
import { UseFormReset } from 'react-hook-form';
import { endOfMonth, format, fromUnixTime, startOfMonth } from 'date-fns';
import {
  useReportsFiltersDispatch,
  useReportsViewMode,
} from '../../context/reports/useReportsCtx';
import { useSelector } from 'react-redux';
import { selectProfile } from '../../store/profileSlice';
import { Employee } from '../../models/employee';
import { useUserRoles } from '../useUserRoles';

const today = new Date();

interface UseFiltersValuesResponseType {
  isLoading: boolean;
}

interface FilterDataProps {
  resetForm: UseFormReset<FiltersFormData>;
}

export function useFiltersDefaultValues({
  resetForm,
}: FilterDataProps): UseFiltersValuesResponseType {
  const dispatchFilters = useReportsFiltersDispatch();
  const [viewMode] = useReportsViewMode();
  const { isAdmin } = useUserRoles();
  const [searchParams] = useSearchParams();
  const userProfile = useSelector(selectProfile);

  const isInitialRender = useRef(true);
  const [isLoading, setIsLoading] = useState(true);

  const queryParams = useMemo(
    () => Object.fromEntries(new URLSearchParams(searchParams)),
    [searchParams]
  );

  const fetchData = useCallback(async () => {
    let managerPromise = queryParams.managerId
      ? getEmployee(queryParams.managerId)
      : null;

    if (!isAdmin && userProfile) {
      managerPromise = Promise.resolve(userProfile as unknown as Employee);
    }

    const employeePromise = queryParams.employeeId
      ? getEmployee(queryParams.employeeId)
      : null;

    const projectPromise = queryParams.projectId
      ? getProjectById(queryParams.projectId)
      : null;

    const towerPromisse = queryParams.towerId
      ? getTowerById(queryParams.towerId)
      : null;

    const [employee, manager, project, tower ] = await Promise.all([
      employeePromise,
      managerPromise,
      projectPromise,
      towerPromisse,
    ]);

    const unixStartDate = queryParams.startDate
      ? fromUnixTime(Number(queryParams.startDate))
      : startOfMonth(today);
    const unixEndDate = queryParams.endDate
      ? fromUnixTime(Number(queryParams.endDate))
      : endOfMonth(today);

    const monthSelect =
      (queryParams.monthSelect as MonthSelect) || MonthSelect.CURRENT;

    const defaultValues: FiltersFormData = {
      startDate: unixStartDate,
      endDate: unixEndDate,
      monthSelect,
      manager: manager || null,
      project: project || null,
      tower:  tower || null,
      engineer: employee || null,
    };

    resetForm(defaultValues);

    dispatchFilters({
      employeeId: employee?.id || null,
      employeeName: employee?.name || null,
      employeeLocation: employee?.location || '',
      managerId: manager?.id || null,
      managerName: manager?.name || null,
      projectId: project?.id || null,
      projectName: project?.name || null,
      projectCode: project?.code || null,
      towerId: tower?.id || null,
      startDate: format(unixStartDate, 'yyyy-MM-dd'),
      endDate: format(unixEndDate, 'yyyy-MM-dd'),
      monthSelect,
    });

    isInitialRender.current = false;
    setIsLoading(false);
  }, [dispatchFilters, isAdmin, queryParams, resetForm, userProfile]);

  useEffect(() => {
    if (isInitialRender.current) {
      fetchData();
    }
  }, [fetchData, queryParams]);

  return { isLoading };
}
